import React, { FC, memo, useContext, useEffect, useState } from 'react'

import { Select } from 'antd'
import { debounce } from 'lodash-es'
import { useFormContext } from 'react-hook-form'

import { AppContext } from 'Const'
import useEvent from 'Hooks/useEvent'
import useToggle from 'Hooks/useToggle'
import { fetchCities, fetchAddresses, getPlaceDetails } from 'Utils/google'

interface Option {
  address: string
  label: string
  value: string
}

interface Props {
  calcTransportCost: () => void
  className?: string
  id?: string
  nameGroupPrefix: string
}

const AddressSelect: FC<Props> = ({ calcTransportCost, className, nameGroupPrefix, ...props }) => {
  const { googlePlacesApiKey } = useContext(AppContext)
  const { setValue, register, watch, getValues } = useFormContext()
  const [options, setOptions] = useState([] as Option[])
  const [loading, , loadStatus, loadedStatus] = useToggle()

  const name = `${nameGroupPrefix}[address1]`
  const cityFieldName = `${nameGroupPrefix}[city]`
  const cityGooglePlaceIdFieldName = `${nameGroupPrefix}[cityGooglePlaceId]`
  const countryFieldName = `${nameGroupPrefix}[countryIsoCode]`
  const stateFieldName = `${nameGroupPrefix}[stateIsoCode]`
  const zipCodeFieldName = `${nameGroupPrefix}[zipCode]`
  const latitudeFieldName = `${nameGroupPrefix}[latitude]`
  const longitudeFieldName = `${nameGroupPrefix}[longitude]`

  const address = watch(name)

  useEffect(() => {
    register({ name }, { required: 'Address is required' })

    if (address === undefined) setValue(name, '')
  }, [name, register, address, setValue, latitudeFieldName, longitudeFieldName])

  const handleSelect = useEvent((placeId: string, option: Option) => {
    const select = async () => {
      const { address } = option
      setValue(name, address)

      const placeDetails = await getPlaceDetails(
        {
          placeId,
          fields: ['address_components', 'geometry.location'],
        },
        googlePlacesApiKey,
      )
      const addressComponents = placeDetails.address_components || []
      const location = placeDetails.geometry?.location
      const countryData = addressComponents.find(({ types }) => types.includes('country'))
      if (countryData) setValue(countryFieldName, countryData.short_name)
      const stateData = addressComponents.find(({ types }) => types.includes('administrative_area_level_1'))
      if (stateData) setValue(stateFieldName, stateData.short_name)
      const cityData = addressComponents.find(
        ({ types }) => types.includes('locality') || types.includes('postal_town'),
      )
      if (cityData && countryData) {
        const cities = await fetchCities(countryData.short_name, cityData.short_name, googlePlacesApiKey)
        const cityPlace = cities.find((city) => city.value === cityData.short_name)
        if (cityPlace) {
          setValue(cityFieldName, cityPlace.value)
          setValue(
            cityGooglePlaceIdFieldName,
            getValues('fulfillment.kind') === 'shipping' ? cityPlace.placeId : placeId,
          )
        } else {
          setValue(cityFieldName, null)
          setValue(cityGooglePlaceIdFieldName, null)
        }
      }
      const zipCodeData = addressComponents.find(({ types }) => types.includes('postal_code'))
      if (zipCodeData) setValue(zipCodeFieldName, zipCodeData.long_name)
      if (location) {
        setValue(latitudeFieldName, location.lat())
        setValue(longitudeFieldName, location.lng())
      }

      calcTransportCost()
    }

    void select()
  })

  const handleSearch = useEvent(
    debounce((query: string) => {
      const search = async () => {
        loadStatus()
        const addresses = await fetchAddresses(query, googlePlacesApiKey)
        loadedStatus()
        setOptions(addresses.map((a) => ({ value: a.placeId, address: a.value, label: a.label })))
      }

      void search()
    }, 500),
  )

  const setAsFloat = useEvent((rawValue: unknown) => {
    const value = parseFloat(`${rawValue}`)
    return isNaN(value) ? 0 : value
  })

  return (
    <>
      <Select
        showSearch
        className={`w-100 ${className}`}
        filterOption={false}
        loading={loading}
        options={options}
        placeholder="Select your address"
        size="large"
        value={address}
        onSearch={handleSearch}
        onSelect={handleSelect}
        {...props}
      />
      <input
        ref={register({ required: "Sorry, address isn't supported", setValueAs: setAsFloat })}
        name={latitudeFieldName}
        type="hidden"
      />
      <input
        ref={register({ required: "Sorry, address isn't supported", setValueAs: setAsFloat })}
        name={longitudeFieldName}
        type="hidden"
      />
    </>
  )
}

export default memo(AddressSelect)
